import axios, { AxiosResponse } from 'axios'
import i18n from 'i18next'
import { decode } from 'html-entities'
import { jwtDecode } from 'jwt-decode'
import { getCookie } from '../context/impersonationContext'

// Interface for JWT token structure
interface TokenPayload {
  'https://eacq.app.com/isImpersonating'?: boolean
  'https://eacq.app.com/impersonateData'?: {
    as: {
      user_id: string
      email: string
      name: string
      picture: string
    }
    at: string
    by: {
      user_id: string
      email: string
      name: string
    }
  }
  [key: string]: any
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_URL_API,
  withCredentials: true,
  headers: {
    'Content-type': 'application/json',
    'x-api-key': process.env.REACT_APP_API_KEY,
    'Accept-Language': i18n.language ?? 'fr',
  },
})

// Function to get impersonated user ID for API headers
const getImpersonatedUserId = (): string | null => {
  try {
    // Check for personnification delegation token first (new system)
    const delegationToken = localStorage.getItem('delegation_id_token')
    if (delegationToken) {
      try {
        const delegationPayload = jwtDecode<TokenPayload>(delegationToken)
        // Personnification delegation token contains the impersonated user's ID in sub
        if (delegationPayload.sub) {
          return delegationPayload.sub
        }
      } catch (delegationError) {
        console.error('Error decoding delegation token:', delegationError)
      }
    }
    
    // Fall back to legacy impersonation system (Auth0 claims)
    const cookieToken = getCookie('id_token')
    if (!cookieToken) {
      return null
    }
    
    const payload = jwtDecode<TokenPayload>(cookieToken)
    const isImpersonating = payload['https://eacq.app.com/isImpersonating']
    const impersonateData = payload['https://eacq.app.com/impersonateData']
    
    if (isImpersonating && impersonateData?.as?.user_id) {
      return impersonateData.as.user_id
    }
    
    return null
  } catch (error) {
    console.error('Error decoding token for impersonation:', error)
    return null
  }
}

export const Mock = axios.create({
  baseURL: 'https://bd880442-fd00-43eb-baaf-c2ce41fe9cc2.mock.pstmn.io',
  headers: {
    'Content-type': 'application/json',
    Accept: '*/*',
    'x-api-key': 'PMAK-63e2cff5620bbc53d4ec91c1-5cbe4de1b2dde540471d423c62b0b3048c',
  },
})

const addUtf8Interceptors = (axiosInstance: typeof instance) => {
  
  axiosInstance.interceptors.request.use(
    (config) => {
      // Don't add delegation headers to personnification endpoints themselves
      const isPersonnificationEndpoint = config.url?.includes('/personnification/')
      
      if (!isPersonnificationEndpoint) {
        // Check for delegation token (personnification active)
        const delegationToken = localStorage.getItem('delegation_auth_token')
        const isImpersonating = localStorage.getItem('is_impersonating') === 'true'
        
        if (delegationToken && isImpersonating) {
          // Replace Authorization header with delegation token
          config.headers = config.headers || {}
          config.headers['Authorization'] = `Bearer ${delegationToken}`
          
        }
        
        // Add target-user-id header for backward compatibility
        const impersonatedUserId = getImpersonatedUserId()
        if (impersonatedUserId) {
          config.headers = config.headers || {}
          config.headers['target-user-id'] = impersonatedUserId
        }
      }
      
      return config
    },
    error => Promise.reject(error)
  )

 //  Response Interceptor (Decode HTML Entities)
 axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data && typeof response.data === 'object') {
      //  Decode all string values inside response.data
      const decodeObject = (obj: any): any => {
        if (typeof obj === 'string') {
          return decode(obj); // Decode HTML entities
        } else if (Array.isArray(obj)) {
          return obj.map(decodeObject); // Decode arrays
        } else if (typeof obj === 'object' && obj !== null) {
          return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [key, decodeObject(value)])
          );
        }
        return obj;
      };

      response.data = decodeObject(response.data);
    }
    return response;
  },
  error => Promise.reject(error)
);
};


addUtf8Interceptors(instance);
addUtf8Interceptors(Mock);
export default instance
